/**
 * schemas/approvals
 * ──────────────────────────────────────────────────────────────────────────
 * The approval queue and the dual-approval state machine.
 *
 * The rule that shapes every field here: an item stays `pending` until
 * `approved_by.length === needs`. A two-approver item that one person has
 * approved is still pending, still in the queue, and its button reads
 * "Approve (1 of 2)". `progress` is the API's own rendering of that fact.
 *
 * A rejection carries a reason, always, and the requester sees it (invariant 5).
 * ──────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';
import { currency, currencyTotal, isoDate, isoDateNullable, minorNullable } from '../common';

export const APPROVAL_STATES = ['pending', 'approved', 'rejected'] as const;
export type ApprovalState = (typeof APPROVAL_STATES)[number];

export const approvalSchema = z.object({
  id: z.string(),
  /** What is being approved — a spend, a waive, a settlement. */
  kind: z.string(),
  /** The transaction / reconciliation this decision releases. */
  subject_id: z.string(),
  label: z.string().nullable(),
  /** Who asked. */
  who: z.string().nullable(),
  amount_minor: minorNullable,
  cur: currency.nullable(),
  /** The matched rules, joined — the queue's "rule" column reads this verbatim. */
  rule_summary: z.string().nullable(),
  /** How many approvals are required: 1 or 2. */
  needs: z.number(),
  /** The named approvers, in the order they must act. */
  approvers: z.array(z.string()),
  /** Who has approved so far. */
  approved_by: z.array(z.string()),
  state: z.string(),
  /** Required on a rejection, and shown to the requester. */
  reason: z.string().nullable(),
  /** e.g. "1 of 2 approvals" — rendered as a chip on dual items. */
  progress: z.string(),
  created_at: isoDate,
  decided_at: isoDateNullable,
});
export type Approval = z.infer<typeof approvalSchema>;

export const approvalsResponseSchema = z.object({ approvals: z.array(approvalSchema) });

/**
 * GET /v1/approvals/queue-summary — the approver console's three KPI cards.
 * Field names verified against the live API: `pending` and `value`, not
 * `waiting` and `value_in_queue`.
 *
 * IMPORTANT: this is scoped to the CALLING approver — the API filters on
 * `approvers has <caller>` and excludes what they have already approved. So a
 * Finance user reading it sees 0, which is correct and is why the Overview's
 * pending-approvals card counts from its own payload instead.
 */
export const queueSummarySchema = z.object({
  pending: z.number(),
  /** Per currency, largest first. Invariant 4: never one blended number. */
  value: z.array(currencyTotal),
  decided_this_month: z.number(),
  /**
   * Not sent today. Kept optional so the Overview's "oldest …" sub line lights
   * up automatically if the API starts supplying it, rather than needing a
   * schema change first.
   */
  oldest_at: isoDateNullable.optional(),
});
export type QueueSummary = z.infer<typeof queueSummarySchema>;

/**
 * GET /v1/approvals/policy-in-force — verified against the live API.
 *
 * The response is `{ policy, rules }`, not a flat quad: the four numbers are
 * nested under `policy`, and the RULES they seeded come with them. That is more
 * than the design's read-only quad, and it is better — an approver asking "why
 * did this reach me?" is answered by the rule list, not by the thresholds alone.
 */
export const policyInForceSchema = z.object({
  policy: z.object({
    base_currency: currency.nullable(),
    threshold_minor: minorNullable,
    auto_under_minor: minorNullable,
    dual_over_minor: minorNullable,
  }),
  rules: z.array(
    z.object({
      id: z.string(),
      on_field: z.string(),
      value_minor: minorNullable,
      value_text: z.string().nullable(),
      approvers: z.array(z.string()),
      /** The API's own sentence, e.g. "Spend over AED 500.00 needs an approval". */
      summary: z.string().nullable().optional(),
      is_active: z.boolean(),
    }),
  ),
});
export type PolicyInForce = z.infer<typeof policyInForceSchema>;

/** POST /v1/approvals/:id/decision — a rejection MUST carry a reason. */
export const decisionSchema = z
  .object({
    decision: z.enum(['approve', 'reject']),
    reason: z.string().min(1).max(1000).optional(),
  })
  .refine((v) => v.decision !== 'reject' || !!v.reason?.trim(), {
    message: 'A rejection must say why. The requester sees this reason.',
    path: ['reason'],
  });
export type Decision = z.infer<typeof decisionSchema>;

export const decisionResultSchema = z.object({
  id: z.string(),
  state: z.string(),
  approved_by: z.array(z.string()),
  needs: z.number(),
  /** True once the subject actually moved — not merely "approved by one". */
  released: z.boolean(),
  outbox_id: z.string().optional(),
});
export type DecisionResult = z.infer<typeof decisionResultSchema>;

/** POST /v1/approvals/bulk-decision — the queue's "Approve all". */
export const bulkDecisionSchema = z.object({
  ids: z.array(z.string()).min(1).max(200),
  decision: z.enum(['approve', 'reject']),
  reason: z.string().min(1).max(1000).optional(),
});

export const bulkResultSchema = z.object({
  released: z.array(z.string()),
  still_pending: z.array(z.string()),
  rejected: z.array(z.string()),
  /** Items the API refused, each with its reason — reported, never silent. */
  skipped: z.array(z.object({ id: z.string(), error: z.string() })),
});
export type BulkResult = z.infer<typeof bulkResultSchema>;

/**
 * Is this a dual-approval item still waiting for its first decision? The
 * approver queue's button reads "Approve (1 of 2)" in that case, so the person
 * clicking it knows they are not the last word.
 */
export const awaitsSecondApproval = (a: Approval): boolean =>
  a.needs > 1 && a.approved_by.length < a.needs;

/** The button label for one queue row. */
export const approveLabel = (a: Approval): string =>
  awaitsSecondApproval(a) ? `Approve (${a.approved_by.length + 1} of ${a.needs})` : 'Approve';
